import {
  collection,
  doc,
  setDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  increment,
  updateDoc,
  serverTimestamp,
  getDoc,
  runTransaction,
  QueryDocumentSnapshot,
  startAfter,
  onSnapshot
} from 'firebase/firestore';
import {
  getStorage,
  ref,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
  UploadTask,
  StorageReference,
  deleteObject
} from 'firebase/storage';
import { AppUser } from '../types';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth, db } from './firebase';
import { httpsCallable } from 'firebase/functions';
import { functionsClient } from './firebase';
import { MediaHistoryItem } from '../types';
import { initialMediaHistory } from '../mockData';
import { cacheJson, cacheMediaUrl, readCachedJson } from './cacheService';
import { buildSearchIndex } from './searchIndex';
import { recordReadWriteCount, withReadCounter } from '../utils/withReadCounter';
import { dispatchNotificationOptInPrompt } from './notificationService';

const MEDIA_STORAGE_KEY = 'clue200_media_history_cache';
const storage = getStorage();

const normalizeMediaType = (value?: string): string => {
    const normalized = String(value ?? '').trim().toLowerCase();
    if (!normalized) return 'photo';
    if (['long', 'video', 'longs', 'videos', 'long-video'].includes(normalized)) return 'long';
    if (['short', 'shorts', 'minis', 'reel', 'reels'].includes(normalized)) return 'short';
    if (['photo', 'image', 'images', 'post', 'posts'].includes(normalized)) return 'photo';
    if (['live', 'story', 'stream'].includes(normalized)) return 'live';
    return normalized;
};

const matchesMediaType = (itemType: unknown, expectedType?: 'short' | 'long' | 'photo' | 'live') => {
    if (!expectedType) return true;
    const normalizedType = normalizeMediaType(String(itemType ?? ''));
    const aliases: Record<'short' | 'long' | 'photo' | 'live', string[]> = {
        short: ['short', 'shorts', 'reel', 'reels'],
        long: ['long', 'video', 'videos', 'longs', 'long-video'],
        photo: ['photo', 'image', 'images', 'post', 'posts'],
        live: ['live', 'story', 'stream']
    };
    return aliases[expectedType].includes(normalizedType);
};

const normalizeMediaItem = (item: any) => ({
    ...item,
    type: normalizeMediaType(item?.type),
    isDeleted: item?.isDeleted === true
});

export interface UploadProgress {
    progress: number;
    state: 'running' | 'paused' | 'success' | 'error';
    downloadUrl?: string;
    error?: string;
    errorCode?: string;
}

/**
 * Upload file to Firebase Storage with progress tracking
 */
export const uploadFileWithProgress = (
    file: File,
    path: string,
    onProgress: (status: UploadProgress) => void
): UploadTask => {
    if (!auth.currentUser) {
        console.warn('Firebase auth.currentUser is null during upload, proceeding with storage task request...');
    }
    const storageRef = ref(storage, path);
    const uploadTask = uploadBytesResumable(storageRef, file, {
        contentType: file.type || 'application/octet-stream',
        cacheControl: 'public, max-age=31536000, immutable'
    });

    uploadTask.on('state_changed',
        (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            onProgress({
                progress,
                state: snapshot.state === 'paused' ? 'paused' : 'running'
            });
        },
        (error) => {
            console.error('Firebase Storage upload failed:', {
                code: error.code,
                message: error.message
            });
            onProgress({
                progress: 0,
                state: 'error',
                error: error.message,
                errorCode: error.code
            });
        },
        async () => {
            try {
                const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
                onProgress({ progress: 100, state: 'success', downloadUrl });
            } catch (error) {
                const details = error instanceof Error
                    ? {
                        code: 'code' in error && typeof error.code === 'string' ? error.code : 'storage/unknown',
                        message: error.message
                    }
                    : { code: 'storage/unknown', message: String(error) };
                console.error('Firebase Storage download URL lookup failed:', details);
                onProgress({
                    progress: 0,
                    state: 'error',
                    error: details.message,
                    errorCode: details.code
                });
            }
        }
    );

    return uploadTask;
};

export const waitForFirebaseUser = async (timeoutMs = 10000): Promise<User | null> => {
    if (auth.currentUser) return auth.currentUser;
    return new Promise(resolve => {
        let settled = false;
        let unsubscribe = () => {};
        const finish = (user: User | null) => {
            if (settled) return;
            settled = true;
            unsubscribe();
            resolve(user);
        };
        unsubscribe = onAuthStateChanged(auth, user => finish(user));
        window.setTimeout(() => finish(auth.currentUser), timeoutMs);
    });
};

export const removeUploadedFile = async (storagePath: string): Promise<void> => {
    await deleteObject(ref(storage, storagePath));
};

export const uploadProfileAvatar = async (uid: string, file: File): Promise<string> => {
    if (!auth.currentUser || auth.currentUser.uid !== uid) {
        throw new Error('Sign in to change your profile photo.');
    }
    if (!file.type.startsWith('image/') || file.size === 0 || file.size >= 5 * 1024 * 1024) {
        throw new Error('Choose an image smaller than 5 MB.');
    }

    try {
        const photoId = doc(collection(db, 'users', uid, 'profilePhotos')).id;
        const storagePath = `avatars/${uid}/profilePhotos/${photoId}.webp`;
        const avatarRef = ref(storage, storagePath);

        await uploadBytes(avatarRef, file, {
            contentType: file.type || 'image/webp',
            cacheControl: 'public, max-age=31536000, immutable'
        });
        const downloadUrl = await getDownloadURL(avatarRef);

        const historyRef = doc(db, 'users', uid, 'profilePhotos', photoId);
        const userRef = doc(db, 'users', uid);

        await runTransaction(db, async transaction => {
            transaction.set(historyRef, {
                url: downloadUrl,
                storagePath,
                createdAt: serverTimestamp()
            });
            transaction.set(userRef, {
                photoURL: downloadUrl,
                photoUpdatedAt: serverTimestamp(),
                profilePhotoURL: downloadUrl,
                updatedAt: serverTimestamp()
            }, { merge: true });
        });

        return downloadUrl;
    } catch (storageError) {
        console.warn('Firebase Storage upload encountered an error, falling back to data URL:', storageError);
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                if (typeof reader.result === 'string') {
                    resolve(reader.result);
                } else {
                    reject(new Error('Failed to process image file.'));
                }
            };
            reader.onerror = () => reject(new Error('Failed to read image file.'));
            reader.readAsDataURL(file);
        });
    }
};

/**
 * Load media feed with pagination (Section 3 & 4)
 */
export const loadMediaFeed = async (
    type?: 'short' | 'long' | 'photo' | 'live',
    pageSize: number = 10,
    lastDoc?: QueryDocumentSnapshot
) => {
    try {
        const colRef = collection(db, 'media');
        const q = query(colRef, limit(pageSize));

        const snapshot = await withReadCounter(
            'media-feed',
            () => getDocs(q),
            result => ({ reads: result.metadata.fromCache ? 0 : result.size })
        );
        const list: any[] = snapshot.docs.map(docSnap => normalizeMediaItem({ id: docSnap.id, ...docSnap.data() }));

        const activeItems = list
            .filter(item => item.isDeleted !== true)
            .filter(item => !type || matchesMediaType(item.type, type));
        activeItems.sort((a, b) => mediaCreatedAt(b) - mediaCreatedAt(a));
        return {
            items: activeItems.slice(0, pageSize),
            lastDoc: snapshot.docs[snapshot.docs.length - 1],
            hasMore: snapshot.size === pageSize
        };
    } catch (error) {
        console.error('Error loading media feed:', error);
        return { items: [], lastDoc: null, hasMore: false };
    }
};

/**
 * Subscribe to media feed with real-time updates (Present to screen live)
 */
export const subscribeToMediaFeed = (
    type: 'short' | 'long' | 'photo' | 'live',
    pageSize: number = 50,
    callback: (items: any[]) => void
) => {
    const colRef = collection(db, 'media');
    const cached = readCachedJson<Array<{ thumbnailUrl?: string; mediaUrl?: string; authorAvatar?: string }>>(`feed-${type}`);
    if (cached?.length) {
        callback(cached);
        void Promise.all(cached.slice(0, 20).flatMap(item => [item.thumbnailUrl, item.mediaUrl, item.authorAvatar]).map(cacheMediaUrl));
    }
    // Using simple query to minimize index requirements while maintaining live updates
    const q = query(
        colRef,
        where('type', '==', type),
        where('status', '==', 'published'),
        where('isDeleted', '==', false),
        orderBy('createdAt', 'desc'),
        limit(pageSize)
    );

    return onSnapshot(q, (snapshot) => {
        recordReadWriteCount(`feed-${type}`, {
            reads: snapshot.metadata.fromCache ? 0 : snapshot.docChanges().length
        });
        const list: any[] = [];
        snapshot.forEach(docSnap => {
            list.push({ id: docSnap.id, ...docSnap.data() });
        });
        // Sort client-side to avoid needing a composite index for now
        const sorted = list.sort((a, b) => mediaCreatedAt(b) - mediaCreatedAt(a));
        callback(sorted);
        void cacheJson(`feed-${type}`, sorted);
        void Promise.all(sorted.slice(0, 20).flatMap(item => [item.thumbnailUrl, item.mediaUrl, item.authorAvatar]).map(cacheMediaUrl));
    }, (error) => {
        console.error(`Error in ${type} feed subscription:`, error);
        // Fallback: If 'where' fails due to missing index or permissions, try a simpler fetch
        const fallbackQ = query(colRef, limit(pageSize));
        withReadCounter(
            `feed-${type}`,
            () => getDocs(fallbackQ),
            snap => ({ reads: snap.metadata.fromCache ? 0 : snap.size })
        ).then(snap => {
            const filtered: Array<{ id: string; thumbnailUrl?: string; mediaUrl?: string; authorAvatar?: string; status?: string; isDeleted?: boolean; type?: string }> = snap.docs
                .map(d => ({ id: d.id, ...d.data() }) as { id: string; thumbnailUrl?: string; mediaUrl?: string; authorAvatar?: string; status?: string; isDeleted?: boolean; type?: string })
                .filter((item: any) =>
                    item.status === 'published' &&
                    item.isDeleted !== true &&
                    matchesMediaType(item.type, type)
                );
            const sorted = filtered.sort((a: any, b: any) => mediaCreatedAt(b) - mediaCreatedAt(a));
            callback(sorted);
            void cacheJson(`feed-${type}`, sorted);
            void Promise.all(sorted.slice(0, 20).flatMap(item => [item.thumbnailUrl, item.mediaUrl, item.authorAvatar]).map(cacheMediaUrl));
        }).catch(error => {
            console.error(`Fallback ${type} feed fetch failed:`, error);
            callback([]);
        });
    });
};

/**
 * Atomic counter increment (Section 3 & Unified Stats)
 */
export const incrementMediaCount = async (
    mediaId: string,
    field: 'viewCount' | 'shareCount',
    val: number = 1
) => {
    try {
        const docRef = doc(db, 'media', mediaId);
        await withReadCounter(
            'media-engagement',
            () => updateDoc(docRef, { [field]: increment(val) }),
            { writes: 1 }
        );

        // User totals are maintained by the scheduled aggregation job, not by clients.
    } catch (error) {
        console.warn('Increment count failed:', error);
    }
};

export const likeMediaOnce = async (mediaId: string, uid: string): Promise<boolean> => {
    const likeRef = doc(db, 'media', mediaId, 'likes', uid);
    const liked = await runTransaction(db, async transaction => {
        const likeSnap = await transaction.get(likeRef);
        if (likeSnap.exists()) return false;
        transaction.set(likeRef, { uid, createdAt: serverTimestamp() });
        return true;
    });
    if (liked) dispatchNotificationOptInPrompt();
    return liked;
};

export const hasMediaLike = async (mediaId: string, uid: string, screen = 'media'): Promise<boolean> => {
    const likeSnapshot = await withReadCounter(
        screen,
        () => getDoc(doc(db, 'media', mediaId, 'likes', uid)),
        snapshot => ({ reads: snapshot.metadata.fromCache ? 0 : 1 })
    );
    return likeSnapshot.exists();
};

export const hasMediaCommentLike = async (
    mediaId: string,
    commentId: string,
    uid: string,
    screen = 'media'
): Promise<boolean> => {
    const likeSnapshot = await withReadCounter(
        screen,
        () => getDoc(doc(db, 'media', mediaId, 'comments', commentId, 'likes', uid)),
        snapshot => ({ reads: snapshot.metadata.fromCache ? 0 : 1 })
    );
    return likeSnapshot.exists();
};

export const toggleMediaLike = async (mediaId: string, uid: string): Promise<boolean> => {
    const reaction = await toggleMediaReaction(mediaId, uid, 'like');
    return reaction === 'like';
};

export type MediaReaction = 'like' | 'dislike';

export const toggleMediaReaction = async (
    mediaId: string,
    uid: string,
    reaction: MediaReaction
): Promise<MediaReaction | null> => {
    if (!auth.currentUser || auth.currentUser.uid !== uid) {
        throw new Error('Sign in to react to this video.');
    }

    const mediaRef = doc(db, 'media', mediaId);
    const likeRef = doc(db, 'media', mediaId, 'likes', uid);

    const resultReaction = await runTransaction(db, async transaction => {
        const mediaSnap = await transaction.get(mediaRef);
        const likeSnap = await transaction.get(likeRef);

        if (!mediaSnap.exists()) throw new Error('Media not found.');
        const mediaData = mediaSnap.data();
        const currentLikes = Number(mediaData.likeCount || mediaData.likesCount || mediaData.likes || 0);

        if (likeSnap.exists()) {
            const existingReaction = likeSnap.data().reaction || 'like';
            if (existingReaction === reaction) {
                transaction.delete(likeRef);
                const nextLikes = Math.max(0, currentLikes - 1);
                transaction.update(mediaRef, {
                    likeCount: nextLikes,
                    likesCount: nextLikes,
                    likes: nextLikes
                });
                return null;
            } else {
                transaction.set(likeRef, {
                    uid,
                    reaction,
                    updatedAt: serverTimestamp()
                }, { merge: true });
                return reaction;
            }
        } else {
            transaction.set(likeRef, {
                uid,
                reaction,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });
            const nextLikes = currentLikes + 1;
            transaction.update(mediaRef, {
                likeCount: nextLikes,
                likesCount: nextLikes,
                likes: nextLikes
            });
            return reaction;
        }
    });

    if (resultReaction === 'like') dispatchNotificationOptInPrompt();
    return resultReaction;
};

export interface MediaComment {
    id: string;
    uid: string;
    userId: string;
    userName: string;
    userAvatar: string;
    text: string;
    createdAt: unknown;
    likesCount: number;
    isLiked: boolean;
}

export const addMediaComment = async (
    mediaId: string,
    user: Pick<AppUser, 'uid' | 'displayName' | 'photoURL'>,
    text: string
): Promise<string> => {
    if (!auth.currentUser || auth.currentUser.uid !== user.uid) {
        throw new Error('Sign in to comment.');
    }
    const cleanText = text.trim();
    if (!cleanText || cleanText.length > 1000) {
        throw new Error('Comments must be between 1 and 1000 characters.');
    }
    const commentId = doc(collection(db, 'media', mediaId, 'comments')).id;
    const mediaRef = doc(db, 'media', mediaId);
    const commentRef = doc(db, 'media', mediaId, 'comments', commentId);

    await runTransaction(db, async transaction => {
        const mediaSnap = await transaction.get(mediaRef);
        if (!mediaSnap.exists()) throw new Error('Media not found.');
        const mediaData = mediaSnap.data();
        const currentComments = Number(mediaData.commentCount || mediaData.commentsCount || 0);

        transaction.set(commentRef, {
            id: commentId,
            uid: user.uid,
            userId: user.uid,
            userName: user.displayName || 'Member',
            userAvatar: user.photoURL || '',
            text: cleanText,
            createdAt: serverTimestamp(),
            likesCount: 0
        });

        const nextComments = currentComments + 1;
        transaction.update(mediaRef, {
            commentCount: nextComments,
            commentsCount: nextComments
        });
    });

    return commentId;
};

export const deleteMediaComment = async (mediaId: string, commentId: string, uid: string): Promise<void> => {
    if (!auth.currentUser || auth.currentUser.uid !== uid) {
        throw new Error('Sign in to delete your comment.');
    }
    const mediaRef = doc(db, 'media', mediaId);
    const commentRef = doc(db, 'media', mediaId, 'comments', commentId);

    await runTransaction(db, async transaction => {
        const [mediaSnap, commentSnap] = await Promise.all([
            transaction.get(mediaRef),
            transaction.get(commentRef)
        ]);
        if (!commentSnap.exists()) return;
        const commentData = commentSnap.data();
        if (commentData.uid !== uid && commentData.userId !== uid) {
            throw new Error('Unauthorized to delete this comment.');
        }

        transaction.delete(commentRef);

        if (mediaSnap.exists()) {
            const mediaData = mediaSnap.data();
            const currentComments = Number(mediaData.commentCount || mediaData.commentsCount || 0);
            const nextComments = Math.max(0, currentComments - 1);
            transaction.update(mediaRef, {
                commentCount: nextComments,
                commentsCount: nextComments
            });
        }
    });
};

export const toggleMediaCommentLike = async (
    mediaId: string,
    commentId: string,
    uid: string
): Promise<boolean> => {
    if (!auth.currentUser || auth.currentUser.uid !== uid) {
        throw new Error('Sign in to like comments.');
    }
    const commentRef = doc(db, 'media', mediaId, 'comments', commentId);
    const likeRef = doc(db, 'media', mediaId, 'comments', commentId, 'likes', uid);
    const didCreateRelationship = await runTransaction(db, async transaction => {
        const [commentSnapshot, likeSnapshot] = await Promise.all([
            transaction.get(commentRef),
            transaction.get(likeRef)
        ]);
        if (!commentSnapshot.exists()) throw new Error('This comment no longer exists.');

        if (likeSnapshot.exists()) {
            transaction.delete(likeRef);
            return false;
        }

        transaction.set(likeRef, {
            uid,
            createdAt: serverTimestamp()
        });
        return true;
    });
    if (didCreateRelationship) dispatchNotificationOptInPrompt();
    return didCreateRelationship;
};

/**
 * Save media metadata to Firestore after Storage upload succeeds
 */
export const saveMediaDoc = async (mediaData: any) => {
    if (!auth.currentUser) {
        throw new Error('Authentication is required to publish media.');
    }
    const ownerUid = mediaData.ownerUid || auth.currentUser.uid;
    if (auth.currentUser.uid !== ownerUid) {
        throw new Error('Authentication is required to publish media.');
    }
    try {
        const docRef = doc(collection(db, 'media'));
        const normalizedType = normalizeMediaType(mediaData.type);
        const finalData = {
            ...mediaData,
            ownerUid,
            type: normalizedType,
            id: docRef.id,
            searchKeywords: buildSearchIndex(`${mediaData.title || ''} ${mediaData.caption || ''} ${(mediaData.hashtags || []).join(' ')}`),
            createdAt: serverTimestamp(),
            status: "published",
            isDeleted: false,
            ...(normalizedType === 'photo' ? {} : { viewCount: 0 }),
            likeCount: 0,
            dislikeCount: 0,
            commentCount: 0,
            shareCount: 0
        };
        await setDoc(docRef, finalData);

        // Debug Log (Requested in Section 9)
        console.log('Published Media Doc:', {
            mediaId: docRef.id,
            type: finalData.type,
            ownerUid: finalData.ownerUid,
            isDeleted: finalData.isDeleted,
            title: finalData.title
        });

        return docRef.id;
    } catch (error) {
        console.error('Error saving media doc:', {
            code: (error as { code?: string })?.code,
            message: error instanceof Error ? error.message : String(error),
            mediaType: mediaData?.type,
            ownerUid: mediaData?.ownerUid
        });
        throw error;
    }
};

// --- Compatibility with existing code ---

export const subscribeToUserMediaHistory = (
    userId: string,
    callback: (items: MediaHistoryItem[]) => void
) => {
    const colRef = collection(db, 'media');
    const q = query(
        colRef,
        where('ownerUid', '==', userId),
        where('status', '==', 'published'),
        where('isDeleted', '==', false),
        limit(100)
    );

    return onSnapshot(q, (snapshot) => {
        recordReadWriteCount('profile-media-history', {
            reads: snapshot.metadata.fromCache ? 0 : snapshot.docChanges().length
        });
        const list: Array<MediaHistoryItem & { isDeleted?: boolean }> = snapshot.docs.map(docSnap => {
            const data = docSnap.data();
            const normalizedType = normalizeMediaType(data.type);
            return {
                id: docSnap.id,
                title: data.title,
                mediaUrl: data.mediaUrl || data.storagePath,
                thumbnailUrl: data.thumbnailUrl || data.thumbnailStoragePath,
                uploadDate: data.createdAt?.toDate?.()?.toLocaleDateString() || 'Recently',
                views: normalizeMediaType(data.type) === 'photo' ? 0 : (data.viewCount || 0),
                likes: data.likeCount || 0,
                ...data,
                type: normalizedType as MediaHistoryItem['type'],
                isDeleted: data.isDeleted === true
            } as MediaHistoryItem & { isDeleted?: boolean };
        });
        callback(list
          .filter(item => item.isDeleted !== true)
          .sort((a, b) => mediaCreatedAt(b as MediaHistoryItem & { createdAt?: unknown }) - mediaCreatedAt(a as MediaHistoryItem & { createdAt?: unknown })));
    }, error => {
        console.error('Error loading user media history:', error);
        callback([]);
    });
};

const mediaCreatedAt = (item: { createdAt?: unknown }) => {
    if (typeof item.createdAt === 'string') return Date.parse(item.createdAt) || 0;
    if (item.createdAt && typeof item.createdAt === 'object') {
        const timestamp = item.createdAt as { toMillis?: () => number; seconds?: number };
        return timestamp.toMillis?.() || (timestamp.seconds || 0) * 1000;
    }
    return 0;
};

export const saveMediaItem = async (item: MediaHistoryItem, userId?: string): Promise<void> => {
    if (!userId || !auth.currentUser || auth.currentUser.uid !== userId) {
        throw new Error('Authentication is required to save media.');
    }
    try {
        const docRef = doc(db, 'media', item.id);
        await setDoc(docRef, {
            ...item,
            ownerUid: userId,
            type: normalizeMediaType(item.type),
            status: 'published',
            searchKeywords: buildSearchIndex(`${item.title || ''} ${(item.tags || []).join(' ')}`),
            createdAt: serverTimestamp(),
            isDeleted: false,
            ...(normalizeMediaType(item.type) === 'photo' ? {} : { viewCount: item.views || 0 }),
            likeCount: item.likes || 0,
            dislikeCount: 0,
            commentCount: 0,
            shareCount: 0
        }, { merge: true });
    } catch (err) {
        console.warn('Firestore saveMediaItem warning:', err);
    }
};

export const deleteMediaItemFromDb = async (id: string, ownerUid?: string): Promise<void> => {
    const user = auth.currentUser;
    if (!user) throw new Error('Sign in to delete content.');
    const docRef = doc(db, 'media', id);
    const snapshot = await getDoc(docRef);
    if (!snapshot.exists()) throw new Error('This content no longer exists.');
    const data = snapshot.data();
    const isSuperAdminUser = user.email === 'ajaytripathi821@gmail.com';
    if (data.ownerUid !== user.uid && data.uploaderId !== user.uid && !isSuperAdminUser) {
        throw new Error('Only the content owner or admin can delete this item.');
    }

    try {
        if (data.mediaUrl && data.mediaUrl.includes('firebasestorage.googleapis.com')) {
            const pathMatch = data.mediaUrl.match(/\/o\/([^?]+)/);
            if (pathMatch && pathMatch[1]) {
                const decodedPath = decodeURIComponent(pathMatch[1]);
                await removeUploadedFile(decodedPath);
            }
        }
        if (data.thumbnailUrl && data.thumbnailUrl.includes('firebasestorage.googleapis.com')) {
            const thumbMatch = data.thumbnailUrl.match(/\/o\/([^?]+)/);
            if (thumbMatch && thumbMatch[1]) {
                const decodedThumbPath = decodeURIComponent(thumbMatch[1]);
                await removeUploadedFile(decodedThumbPath);
            }
        }
    } catch (e) {
        console.warn('Could not delete storage files for media:', e);
    }

    await updateDoc(docRef, {
        deleted: true,
        isDeleted: true,
        deletedAt: serverTimestamp(),
        deletedBy: user.uid,
        status: 'deleted'
    });
};

export const filterMediaHistory = (
    items: MediaHistoryItem[],
    searchQuery: string,
    selectedTag: string,
    selectedType: 'all' | 'video' | 'image' | 'short'
  ): MediaHistoryItem[] => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    const normalizedTag = selectedTag.trim().toLowerCase();

    return items.filter(item => {
      if (selectedType !== 'all' && item.type !== selectedType) return false;
      if (normalizedTag && normalizedTag !== 'all') {
        const itemTags = (item.tags || []).map(t => t.toLowerCase());
        if (!itemTags.some(t => t === normalizedTag || t.includes(normalizedTag))) return false;
      }
      if (normalizedQuery) {
        const title = (item.title || '').toLowerCase();
        const inTitle = title.includes(normalizedQuery);
        const inTags = (item.tags || []).some(t => (t || '').toLowerCase().includes(normalizedQuery));
        if (!inTitle && !inTags) return false;
      }
      return true;
    });
};

export const extractUniqueTags = (items: MediaHistoryItem[]): string[] => {
    const tagCounts: Record<string, number> = {};
    items.forEach(item => {
      (item.tags || []).forEach(tag => {
        const clean = tag.toLowerCase().trim();
        if (clean) tagCounts[clean] = (tagCounts[clean] || 0) + 1;
      });
    });
    return Object.keys(tagCounts).sort((a, b) => tagCounts[b] - tagCounts[a]);
};

/**
 * Save a comment for a media item
 */
export const saveComment = async (mediaId: string, userId: string, text: string, userName: string): Promise<void> => {
    await addMediaComment(mediaId, {
        uid: userId,
        displayName: userName,
        photoURL: auth.currentUser?.photoURL || ''
    }, text);
};

/**
 * Save a live chat message
 */
export const saveLiveChatMessage = async (streamId: string, userId: string, text: string, userName: string): Promise<void> => {
  try {
    const chatRef = doc(collection(db, 'liveStreams', streamId, 'chat'));
    await setDoc(chatRef, {
      userId,
      userName,
      text,
      createdAt: serverTimestamp()
    });
  } catch (err) {
    console.warn('Firestore saveLiveChatMessage warning:', err);
  }
};

/**
 * Subscribe to total fans count (uniques verified by email/phone)
 */
export const subscribeToFansCount = (
    userId: string,
    callback: (count: number) => void,
    onError?: (error: Error) => void
) => {
    return onSnapshot(doc(db, 'publicProfiles', userId), snapshot => {
        recordReadWriteCount('creator-profile', { reads: snapshot.metadata.fromCache ? 0 : 1 });
        callback(Number(snapshot.get('followerCount')) || 0);
    }, error => {
        if (onError) {
            onError(error);
        } else {
            console.warn('Fans count subscription failed:', error);
            callback(0);
        }
    });
};

/**
 * Keep the follower and following records, plus their counters, in one transaction.
 */
export const followCreator = async (
    creatorUid: string,
    creatorName: string,
    currentUser: AppUser,
    contact?: { email?: string; phone?: string },
    options: { silent?: boolean } = {}
): Promise<boolean> => {
    if (!currentUser?.uid || !creatorUid) throw new Error('Sign in before following this account.');
    if (auth.currentUser?.uid !== currentUser.uid) {
        throw new Error('Your sign-in session is not ready. Please try again.');
    }
    if (currentUser.uid === creatorUid) throw new Error('You cannot follow your own account.');
    const followerRef = doc(db, 'users', creatorUid, 'followers', currentUser.uid);
    const followingRef = doc(db, 'users', currentUser.uid, 'following', creatorUid);
    try {
        const didCreateRelationship = await runTransaction(db, async transaction => {
            const [followerSnap, followingSnap] = await Promise.all([
                transaction.get(followerRef),
                transaction.get(followingRef)
            ]);
            if (followerSnap.exists() !== followingSnap.exists()) {
                throw new Error('Follow records are inconsistent. Please contact support before retrying.');
            }
            if (followerSnap.exists()) return false;

            const createdAt = serverTimestamp();
            transaction.set(followerRef, {
                uid: currentUser.uid,
                displayName: (currentUser.displayName || 'User').slice(0, 100),
                username: (currentUser.username || 'user').slice(0, 32),
                photoURL: (currentUser.photoURL || '').slice(0, 2048),
                createdAt
            });
            transaction.set(followingRef, {
                uid: creatorUid,
                displayName: (creatorName || 'Creator').slice(0, 100),
                createdAt
            });
            transaction.update(doc(db, 'users', currentUser.uid), {
                followingCount: increment(1),
                lastFollowTargetUid: creatorUid
            });
            transaction.update(doc(db, 'users', creatorUid), {
                followerCount: increment(1),
                lastFollowActionUid: currentUser.uid
            });
            transaction.update(doc(db, 'publicProfiles', currentUser.uid), {
                followingCount: increment(1)
            });
            transaction.update(doc(db, 'publicProfiles', creatorUid), {
                followerCount: increment(1)
            });
            return true;
        });
        if (didCreateRelationship) {
            if (!options.silent) {
                dispatchNotificationOptInPrompt();
                dispatchFollowToast(`Following ${creatorName || 'creator'}`, 1000);
            }
        }
        return didCreateRelationship;
    } catch (error) {
        console.error('Could not follow account:', error);
        if (!options.silent) {
            dispatchFollowToast(error instanceof Error ? error.message : 'Could not follow this account.', 3500);
        }
        throw error;
    }
};

export interface SubscriberListItem {
    id: string;
    fanUid: string;
    fanName: string;
    fanUsername: string;
    createdAt?: unknown;
}

export type FollowListType = 'followers' | 'following';

export const getFollowListPage = async (
    userId: string,
    type: FollowListType,
    cursor: QueryDocumentSnapshot | null = null,
    pageSize = 20
): Promise<{ items: SubscriberListItem[]; cursor: QueryDocumentSnapshot | null; hasMore: boolean }> => {
    try {
        const listRef = collection(db, 'users', userId, type);
        let constraints = [
            orderBy('createdAt', 'desc'),
            ...(cursor ? [startAfter(cursor)] : []),
            limit(pageSize + 1)
        ];
        let snapshot;
        try {
            snapshot = await withReadCounter(
                `profile-${type}`,
                () => getDocs(query(listRef, ...constraints)),
                result => ({ reads: result.metadata.fromCache ? 0 : result.size })
            );
        } catch {
            // Fallback without ordering in case index is missing
            snapshot = await getDocs(query(listRef, limit(pageSize + 1)));
        }
        const page = snapshot.docs.slice(0, pageSize);
        return {
            items: page.map(item => {
                const data = item.data();
                return {
                    id: item.id,
                    fanUid: data.uid || item.id,
                    fanName: data.displayName || 'Clue200 member',
                    fanUsername: data.username || '',
                    createdAt: data.createdAt
                };
            }),
            cursor: page.at(-1) ?? cursor,
            hasMore: snapshot.docs.length > pageSize
        };
    } catch (error) {
        console.warn(`getFollowListPage warning for ${type}:`, error);
        return { items: [], cursor: null, hasMore: false };
    }
};

export const subscribeToFollowStatus = (
    followerUid: string,
    creatorUid: string,
    callback: (isFollowing: boolean, createdAt?: unknown) => void
) => {
    if (!followerUid || !creatorUid) return () => {};
    return onSnapshot(
        doc(db, 'users', creatorUid, 'followers', followerUid),
        snapshot => {
            recordReadWriteCount('profile-follow-status', { reads: snapshot.metadata.fromCache ? 0 : 1 });
            callback(snapshot.exists(), snapshot.get('createdAt'));
        },
        error => {
            console.error('Follow status subscription failed:', error);
            dispatchFollowToast('Could not check follow status. Please retry.', 3000);
        }
    );
};

const dispatchFollowToast = (message: string, durationMs: number): void => {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('yetrykar:toast', {
            detail: { message, durationMs }
        }));
    }
};

export const formatCount = (num?: number): string => {
  const n = typeof num === 'number' && Number.isFinite(num) ? num : 0;
  if (n >= 1000000) {
    return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  }
  if (n >= 1000) {
    return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  }
  return n.toLocaleString();
};

export const followUser = async (
    targetUid: string,
    currentUser: AppUser,
    targetName = 'Creator',
    options: { silent?: boolean } = {}
): Promise<boolean> => {
    return followCreator(targetUid, targetName, currentUser, undefined, options);
};

export const checkIsFollowing = async (currentUid: string, targetUid: string): Promise<boolean> => {
    if (!currentUid || !targetUid || currentUid === targetUid) return false;
    const docRef = doc(db, 'users', targetUid, 'followers', currentUid);
    const snap = await getDoc(docRef);
    return snap.exists();
};

export const unfollowUser = async (
    targetUid: string,
    currentUser: AppUser
): Promise<boolean> => {
    return unfollowCreator(targetUid, currentUser);
};

export const unfollowCreator = async (creatorUid: string, currentUser: AppUser): Promise<boolean> => {
    if (!currentUser?.uid || !creatorUid) throw new Error('Sign in before unfollowing this account.');
    if (auth.currentUser?.uid !== currentUser.uid) {
        throw new Error('Your sign-in session is not ready. Please try again.');
    }
    if (currentUser.uid === creatorUid) throw new Error('You cannot unfollow your own account.');
    const followerRef = doc(db, 'users', creatorUid, 'followers', currentUser.uid);
    const followingRef = doc(db, 'users', currentUser.uid, 'following', creatorUid);
    try {
        const didUnfollow = await runTransaction(db, async transaction => {
            const [followerSnap, followingSnap] = await Promise.all([
                transaction.get(followerRef),
                transaction.get(followingRef)
            ]);
            if (followerSnap.exists() !== followingSnap.exists()) {
                throw new Error('Follow records are inconsistent. Please contact support before retrying.');
            }
            if (!followerSnap.exists()) return false;
            transaction.delete(followerRef);
            transaction.delete(followingRef);
            transaction.update(doc(db, 'users', creatorUid), {
                followerCount: increment(-1),
                lastFollowActionUid: currentUser.uid
            });
            transaction.update(doc(db, 'users', currentUser.uid), {
                followingCount: increment(-1),
                lastFollowTargetUid: creatorUid
            });
            transaction.update(doc(db, 'publicProfiles', currentUser.uid), {
                followingCount: increment(-1)
            });
            transaction.update(doc(db, 'publicProfiles', creatorUid), {
                followerCount: increment(-1)
            });
            return true;
        });
        if (didUnfollow) dispatchFollowToast(`Unfollowed ${creatorUid}`, 1000);
        return didUnfollow;
    } catch (error) {
        console.error('Could not unfollow account:', error);
        dispatchFollowToast(error instanceof Error ? error.message : 'Could not unfollow this account.', 3500);
        throw error;
    }
};
function saveCachedMedia(items: MediaHistoryItem[]): void {
  try {
    localStorage.setItem(MEDIA_STORAGE_KEY, JSON.stringify(items));
  } catch {}
}

function getCachedMedia(): MediaHistoryItem[] | null {
  try {
    const data = localStorage.getItem(MEDIA_STORAGE_KEY);
    if (data) return JSON.parse(data) as MediaHistoryItem[];
  } catch {}
  return null;
}
